import { X } from "lucide-react";
import { Dialog } from "radix-ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";

/**
 * A bottom sheet on phones, a centred dialog on desktop (D20): radius 24 on top, a grab handle, a 52 px header and an
 * optional sticky footer for the main action. Focus is trapped and returns to the trigger (radix Dialog). Opened
 * through `useUrlSheet`, the Android Back gesture closes the sheet instead of leaving the page.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  children,
  footer,
  size = "auto",
  wide = false,
  closeLabel,
  onInteractOutside,
  focusInput = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  /** The main action, kept visible at the bottom (e.g. «Показать 124 товара»). */
  footer?: ReactNode;
  /** `tall`: 92 % of the screen on phones (filters, login); `auto`: as tall as the content. */
  size?: "auto" | "tall";
  /** Desktop width 880 px instead of 560 px (a map, a table). */
  wide?: boolean;
  closeLabel?: string;
  onInteractOutside?: (event: Event) => void;
  /**
   * Focus the first field on open (login: the keyboard comes up at once). Otherwise the panel itself takes the
   * focus: screen readers start at the title, and phones do not open the keyboard or ring the close button.
   */
  focusInput?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-night/40 data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
        <Dialog.Content
          aria-describedby={undefined}
          onInteractOutside={onInteractOutside}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            const panel = event.currentTarget as HTMLElement;
            const field = focusInput
              ? panel.querySelector<HTMLElement>("input:not([type=hidden]), select, textarea")
              : null;
            (field ?? panel).focus();
          }}
          className={cn(
            "fixed z-50 flex flex-col bg-surface outline-none",
            // phones: bottom sheet
            "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-3xl data-[state=closed]:animate-sheet-down data-[state=open]:animate-sheet-up",
            size === "tall" && "h-[92dvh]",
            // desktop: centred dialog
            wide ? "lg:w-[min(880px,calc(100vw-2rem))]" : "lg:w-[min(560px,calc(100vw-2rem))]",
            "lg:inset-x-auto lg:top-1/2 lg:bottom-auto lg:left-1/2 lg:h-auto lg:max-h-[calc(100dvh-4rem)] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-3xl lg:data-[state=closed]:animate-dialog-out lg:data-[state=open]:animate-dialog-in",
          )}
        >
          <div aria-hidden className="flex justify-center pt-2 lg:hidden">
            <span className="h-1 w-9 rounded-full bg-line-strong" />
          </div>
          <div className="flex h-13 shrink-0 items-center justify-between gap-3 pr-2 pl-4 lg:h-16 lg:pr-4 lg:pl-6">
            <Dialog.Title className="truncate text-title-sm font-semibold text-ink-strong">
              {title}
            </Dialog.Title>
            <Dialog.Close
              aria-label={closeLabel ?? t("close")}
              className="flex size-11 items-center justify-center rounded-full text-ink hover:bg-canvas"
            >
              <X className="size-5" aria-hidden />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 lg:px-6 lg:pb-6">
            {children}
          </div>
          {footer && (
            <div className="shrink-0 border-t border-line bg-surface pb-safe">
              <div className="px-4 py-3 lg:px-6 lg:py-4">{footer}</div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
